import { useId, useState } from 'react';
import type { LocalizedText } from '@neuroedge/shared';

/**
 * One customer-facing text field that can carry translations
 * (`LocalizedText`: a plain string, or `{en, hi, mr, ...}`). Shows only the
 * main (English) text by default; translations sit behind a "Translations"
 * toggle so the common single-language case stays one simple box. Any
 * language already present on the value (not just hi/mr) is kept and
 * editable. With no translation filled in, the value is saved as a plain
 * string - same contract the server and the rest of the app already use.
 *
 * `variables` renders one-click `{{name}}` chips that append to the main text.
 */

const DEFAULT_EXTRA_LANGUAGES: { code: string; label: string }[] = [
  { code: 'hi', label: 'Hindi' },
  { code: 'mr', label: 'Marathi' },
];

function split(value: LocalizedText | undefined): { base: string; extra: Record<string, string> } {
  if (value === undefined) return { base: '', extra: {} };
  if (typeof value === 'string') return { base: value, extra: {} };
  const { en, ...rest } = value;
  if (en !== undefined) return { base: en, extra: rest };
  const [firstKey, ...others] = Object.keys(value);
  if (!firstKey) return { base: '', extra: {} };
  return {
    base: value[firstKey] ?? '',
    extra: Object.fromEntries(others.map((k) => [k, value[k] ?? ''])),
  };
}

function join(base: string, extra: Record<string, string>): LocalizedText {
  const filled = Object.fromEntries(Object.entries(extra).filter(([, v]) => v.trim() !== ''));
  return Object.keys(filled).length === 0 ? base : { en: base, ...filled };
}

export function LocalizedTextInput({
  label,
  value,
  onChange,
  multiline = false,
  maxLength,
  placeholder,
  variables,
  hint,
}: {
  label: string;
  value: LocalizedText | undefined;
  onChange: (next: LocalizedText) => void;
  multiline?: boolean;
  maxLength?: number;
  placeholder?: string;
  variables?: string[];
  hint?: string;
}) {
  const id = useId();
  const { base, extra } = split(value);
  const languages = [
    ...DEFAULT_EXTRA_LANGUAGES,
    ...Object.keys(extra)
      .filter((code) => !DEFAULT_EXTRA_LANGUAGES.some((l) => l.code === code))
      .map((code) => ({ code, label: code })),
  ];
  const translatedCount = Object.values(extra).filter((v) => v.trim()).length;
  const [showTranslations, setShowTranslations] = useState(translatedCount > 0);

  const setBase = (next: string) => onChange(join(next, extra));
  const setExtra = (code: string, next: string) => onChange(join(base, { ...extra, [code]: next }));

  const inputProps = {
    id,
    className: 'ne-input',
    value: base,
    maxLength,
    placeholder,
  };

  return (
    <div className="ne-field">
      <label className="ne-field__label" htmlFor={id}>
        {label}
      </label>
      {multiline ? (
        <textarea {...inputProps} rows={3} onChange={(e) => setBase(e.target.value)} />
      ) : (
        <input {...inputProps} onChange={(e) => setBase(e.target.value)} />
      )}
      {hint ? (
        <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          {hint}
        </span>
      ) : null}
      {variables && variables.length > 0 ? (
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)', alignItems: 'center' }}
        >
          <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            Insert:
          </span>
          {variables.map((v) => (
            <button
              key={v}
              type="button"
              className="ne-badge"
              title={`Insert {{${v}}}`}
              onClick={() => setBase(`${base}{{${v}}}`)}
            >
              {`{{${v}}}`}
            </button>
          ))}
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => setShowTranslations((s) => !s)}
        aria-expanded={showTranslations}
        style={{
          all: 'unset',
          cursor: 'pointer',
          color: 'var(--color-primary)',
          fontSize: 'var(--font-size-sm)',
        }}
      >
        {showTranslations ? '▾' : '▸'} Translations{translatedCount ? ` (${translatedCount})` : ''}
      </button>
      {showTranslations ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {languages.map((lang) => {
            const props = {
              className: 'ne-input',
              value: extra[lang.code] ?? '',
              maxLength,
              'aria-label': `${label} in ${lang.label}`,
              placeholder: `${lang.label} (optional)`,
            };
            return multiline ? (
              <textarea
                key={lang.code}
                {...props}
                rows={2}
                onChange={(e) => setExtra(lang.code, e.target.value)}
              />
            ) : (
              <input
                key={lang.code}
                {...props}
                onChange={(e) => setExtra(lang.code, e.target.value)}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
